import { getFeatureFlagValue } from '@experiences/feature-flags';
import type { IUiDialogHookCustomContent } from '@experiences/interfaces';
import {
    UiProgressButton,
    UiText,
} from '@experiences/ui-common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Button,
    IconButton,
    Tooltip,
} from '@mui/material';
import { ApAlertBar } from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import {
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import { notificationType } from '../../../common/constants/Constant';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import {
    getEnforcementConfigForServices,
    updateSoftEnforcementSettings,
} from '../../../services/licensing/LicenseService';
import { UiChartLoading } from '../helperComponents/UiConsumableCard/UiChartLoading';
import { AllocationEnforcementServiceCard } from './AllocationEnforcementServiceCard';

interface IService {
    code: string;
    supportsEnforcement: boolean; // true = enforcement available, false = always soft enforcement
    isConfigurable: boolean; // true = user can change, false = read-only
}

interface IServiceEnforcement {
    serviceCode: string;
    softEnforcementEnabled: boolean; // true = consumes from org pool, false = stops at 100%
    supportsEnforcement: boolean; // true = enforcement available, false = always soft enforcement
    isConfigurable: boolean; // true = user can change, false = read-only (disabled checkbox)
}

export interface IStopUsageSaveSummary {
    previousStopped: number;
    newStopped: number;
    total: number;
}

interface IAllocationEnforcementDialogContentProps {
    tenantId: string;
    tenantName: string;
    consumableCode: string;
    onSaved?: (summary: IStopUsageSaveSummary) => void;
}

type IAllocationEnforcementDialogProps = IUiDialogHookCustomContent & IAllocationEnforcementDialogContentProps;

interface IFormData {
    services: Record<string, boolean>; // sanitized serviceCode -> softEnforcementEnabled
}

// Helper function to sanitize service codes for use as form field names
const sanitizeServiceCode = (serviceCode: string): string => serviceCode.replace(/\./g, '_');

// Helper function to create service configurations
const createService = (code: string, supportsEnforcement: boolean, isConfigurable: boolean): IService => ({
    code,
    supportsEnforcement,
    isConfigurable,
});

// Reusable service definitions
const CONFIGURABLE_SERVICES = {
    agents: createService('agents', true, true),
    healingagent: createService('healingagent', true, true),
    ixp: createService('ixp', true, true),
    semantic: createService('semantic', true, true),
    ecs: createService('ecs', true, true),
    processmining: createService('processmining', true, true),
    jarvis: createService('jarvis', true, true),
    serverless: createService('serverless', true, true),
    hypervisor: createService('hypervisor', true, true),
    testmanager: createService('testmanager', true, true),
    uia: createService('uia', true, true),
};

const NON_CONFIGURABLE_SERVICES = {
    genai: createService('integrationservice.genai', false, false),
    ao: createService('ao', false, false), // Always soft enforcement
    documentunderstanding: createService('documentunderstanding', false, false), // Always soft enforcement
    taskmining: createService('taskmining', false, false), // Always soft enforcement
    aicenter: createService('aicenter', false, false), // Always soft enforcement
    serverlessLocked: createService('serverless', true, false), // Always hard enforcement (RU)
    hypervisorLocked: createService('hypervisor', true, false), // Always hard enforcement (RU)
};

// Mapping of consumable codes to their available services
export const consumableServicesMap: Record<string, IService[]> = {
    AGU: [
        CONFIGURABLE_SERVICES.agents,
        CONFIGURABLE_SERVICES.healingagent,
        CONFIGURABLE_SERVICES.uia,
        NON_CONFIGURABLE_SERVICES.ao,
    ],
    AIU: [
        CONFIGURABLE_SERVICES.ixp,
        CONFIGURABLE_SERVICES.semantic,
        CONFIGURABLE_SERVICES.ecs,
        CONFIGURABLE_SERVICES.processmining,
        CONFIGURABLE_SERVICES.jarvis,
        NON_CONFIGURABLE_SERVICES.genai,
        NON_CONFIGURABLE_SERVICES.documentunderstanding,
        NON_CONFIGURABLE_SERVICES.taskmining,
        NON_CONFIGURABLE_SERVICES.aicenter,
    ],
    RU: [
        NON_CONFIGURABLE_SERVICES.serverlessLocked,
        NON_CONFIGURABLE_SERVICES.hypervisorLocked,
    ],
    PLTU: [
        CONFIGURABLE_SERVICES.agents,
        CONFIGURABLE_SERVICES.healingagent,
        CONFIGURABLE_SERVICES.ixp,
        CONFIGURABLE_SERVICES.semantic,
        CONFIGURABLE_SERVICES.ecs,
        CONFIGURABLE_SERVICES.processmining,
        CONFIGURABLE_SERVICES.jarvis,
        CONFIGURABLE_SERVICES.serverless,
        CONFIGURABLE_SERVICES.hypervisor,
        CONFIGURABLE_SERVICES.uia,
        NON_CONFIGURABLE_SERVICES.genai,
        NON_CONFIGURABLE_SERVICES.ao,
        NON_CONFIGURABLE_SERVICES.documentunderstanding,
        NON_CONFIGURABLE_SERVICES.taskmining,
        NON_CONFIGURABLE_SERVICES.aicenter,
    ],
    TEU: [ CONFIGURABLE_SERVICES.testmanager ],
    HEAL: [ CONFIGURABLE_SERVICES.healingagent ],
    SPR: [ CONFIGURABLE_SERVICES.uia ],
};

export const applyFeatureFlagOverrides = (services: IService[]): IService[] => services.map(service => {
    const serviceOverride = { ...service };
    const serviceFlagKey = `EnableAllocationEnforcement.${service.code}`;
    const serviceFeatureFlagEnabled = getFeatureFlagValue(serviceFlagKey);

    if (serviceFeatureFlagEnabled) {
        if (!service.isConfigurable) {
            serviceOverride.isConfigurable = true;
            serviceOverride.supportsEnforcement = true;
        }
    }

    return serviceOverride;
});

export const AllocationEnforcementDialogBody: React.FC<IAllocationEnforcementDialogProps> = ({
    tenantId,
    tenantName,
    consumableCode,
    closeDialog,
    onSaved,
}) => {
    const { formatMessage: translate } = useIntl();
    const { createNotification } = useUiSnackBar();
    const [ localServices, setLocalServices ] = useState<IServiceEnforcement[]>([]);
    const [ initialValues, setInitialValues ] = useState<Record<string, boolean>>({});
    const [ isLoadingData, setIsLoadingData ] = useState(true);

    const methods = useForm<IFormData>({
        mode: 'onChange',
        defaultValues: { services: {} },
    });

    const {
        handleSubmit, reset, formState, watch, setValue,
    } = methods;
    const {
        isSubmitting, isDirty,
    } = formState;

    const loadServiceEnforcement = useCallback(async (): Promise<IServiceEnforcement[]> => {
        try {
            const derived = await getEnforcementConfigForServices({
                tenantId,
                unit: consumableCode,
            });
            if (derived.serviceConfiguration?.length) {
                return derived.serviceConfiguration.map(s => ({
                    serviceCode: s.service,
                    supportsEnforcement: true,
                    isConfigurable: s.isConfigurable ?? false,
                    softEnforcementEnabled: s.isConfigurable ? s.softEnforcementEnabled : true,
                }));
            }
        } catch {
            // TODO(remove one sprint after LA changes)
        }

        const availableServices = applyFeatureFlagOverrides(consumableServicesMap[consumableCode] || []);
        const configurableServiceCodes = availableServices.filter(s => s.isConfigurable).map(s => s.code);
        const softEnforcedServices: string[] = configurableServiceCodes.length > 0
            ? (await getEnforcementConfigForServices({
                tenantId,
                unit: consumableCode,
                services: configurableServiceCodes,
            })).serviceConfiguration.filter(s => s.softEnforcementEnabled).map(e => e.service)
            : [];

        return availableServices.map(service => ({
            serviceCode: service.code,
            supportsEnforcement: service.supportsEnforcement,
            isConfigurable: service.isConfigurable,
            softEnforcementEnabled: !service.supportsEnforcement || softEnforcedServices.includes(service.code),
        }));
    }, [ tenantId, consumableCode ]);

    // Fetch enforcement configuration from backend
    useEffect(() => {
        const fetchEnforcementConfig = async () => {
            setIsLoadingData(true);
            try {
                const servicesWithConfig = (await loadServiceEnforcement())
                    .sort((a, b) => Number(b.isConfigurable) - Number(a.isConfigurable) || a.serviceCode.localeCompare(b.serviceCode));

                setLocalServices(servicesWithConfig);

                // Initialize form with fetched data (using sanitized field names)
                const initialFormData: Record<string, boolean> = {};
                servicesWithConfig.forEach(service => {
                    initialFormData[sanitizeServiceCode(service.serviceCode)] = service.softEnforcementEnabled;
                });
                setInitialValues(initialFormData);
                reset({ services: initialFormData });
            } catch (error) {
                console.error('Failed to fetch enforcement configuration:', error);
                createNotification(
                    translate({ id: 'CLIENT_FAILED_TO_FETCH_ENFORCEMENT_CONFIGURATION' }),
                    notificationType.ERROR
                );
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchEnforcementConfig();
    }, [ tenantId, consumableCode, loadServiceEnforcement ]);

    const handleToggle = useCallback((serviceCode: string) => {
        const sanitizedCode = sanitizeServiceCode(serviceCode);
        const fieldKey = `services.${sanitizedCode}` as const;
        const currentValue = watch(fieldKey);
        setValue(fieldKey, !currentValue, { shouldDirty: true });
    }, [ watch, setValue ]);

    const onSubmit = useCallback(async (data: IFormData) => {
        try {
            // Find only the configurable services that have changed
            const changedServices = localServices
                .filter(service => {
                    // Only consider configurable services
                    if (!service.isConfigurable) {
                        return false;
                    }
                    // Check if the value changed from initial (using sanitized keys)
                    const sanitizedCode = sanitizeServiceCode(service.serviceCode);
                    const currentValue = data.services[sanitizedCode];
                    const initialValue = initialValues[sanitizedCode];
                    return currentValue !== initialValue;
                })
                .map(service => ({
                    service: service.serviceCode, // Use original service code for API
                    softEnforcementEnabled: data.services[sanitizeServiceCode(service.serviceCode)],
                }));

            // Only call API if there are changes
            if (changedServices.length > 0) {
                await updateSoftEnforcementSettings({
                    tenantId,
                    unit: consumableCode,
                    services: changedServices,
                });

                createNotification(
                    translate({ id: 'CLIENT_CONFIGURATION_SUCCESSFULLY_SAVED' }),
                    notificationType.SUCCESS
                );
            }

            const countStopped = (softEnabledOf: (s: IServiceEnforcement) => boolean) =>
                localServices.filter(s => s.supportsEnforcement && !softEnabledOf(s)).length;

            onSaved?.({
                previousStopped: countStopped(s => s.softEnforcementEnabled),
                newStopped: countStopped(s => (s.isConfigurable
                    ? data.services[sanitizeServiceCode(s.serviceCode)] ?? s.softEnforcementEnabled
                    : s.softEnforcementEnabled)),
                total: localServices.length,
            });
            closeDialog(true);
        } catch (error) {
            console.error('Failed to save enforcement configuration:', error);
            createNotification(
                translate({ id: 'CLIENT_FAILED_SAVE_ENFORCEMENT_CONFIGURATION' }),
                notificationType.ERROR
            );
        }
    }, [ localServices, initialValues, tenantId, consumableCode, tenantName, createNotification, translate, closeDialog, onSaved ]);

    if (isLoadingData) {
        return (
            <UiChartLoading
                width={150}
                height={36} />
        );
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{
                    pt: 0,
                    px: 3,
                    pb: 3,
                }}>
                    <ApAlertBar
                        status="warning"
                        cancelable={false}
                        style={{ marginBottom: '16px' }}>
                        {translate({ id: 'CLIENT_ALLOCATION_ENFORCEMENT_WARNING' })}
                    </ApAlertBar>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                    }}>
                        <Tooltip title={translate({ id: `CLIENT_ALLOCATION_ENFORCEMENT_INFO` })}>
                            <IconButton
                                size="small"
                                sx={{ p: 0.5 }}>
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <UiText>
                            {tenantName}
                            {' - '}
                            {translate({ id: `CLIENT_CONSUMABLE_USAGE_HEADER_${consumableCode}` })}
                        </UiText>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <UiText>
                            {translate({ id: `CLIENT_ALLOCATION_ENFORCEMENT_MESSAGE` })}
                        </UiText>
                    </Box>

                    <Box sx={{
                        mb: 3,
                        display: 'grid',
                        gridTemplateColumns: localServices.length === 1
                            ? '1fr'
                            : localServices.length === 2
                                ? 'repeat(2, 1fr)'
                                : 'repeat(3, 1fr)',
                        gap: 2,
                    }}>
                        {localServices.map(service => {
                            const sanitizedCode = sanitizeServiceCode(service.serviceCode);
                            const checked = !watch(`services.${sanitizedCode}`);
                            return (
                                <AllocationEnforcementServiceCard
                                    key={service.serviceCode}
                                    serviceCode={service.serviceCode}
                                    checked={checked}
                                    disabled={!service.isConfigurable}
                                    onChange={() => handleToggle(service.serviceCode)}
                                />
                            );
                        })}
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}>
                        <Button
                            onClick={() => closeDialog(false)}
                            disabled={isSubmitting}
                            data-cy="allocation-enforcement-cancel">
                            {translate({ id: 'CLIENT_CANCEL' })}
                        </Button>
                        <UiProgressButton
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                            disabled={!isDirty}
                            data-cy="allocation-enforcement-save">
                            {translate({ id: 'CLIENT_SAVE' })}
                        </UiProgressButton>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};
